import * as React from "react";
import axios from "axios";
import { useAuthFetch } from "../../hooks/useAuthFetch";

export function ContactActions({ contact, sectionType, onSuccess = () => {}, disableActions }) {
  const { getAuthConfig, isReady } = useAuthFetch();
  const [showMenu, setShowMenu] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    const handleClickOutside = () => {
      setShowMenu(false);
      setShowDeleteConfirm(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (disableActions) return null;

  const handleEdit = () => {
    const event = new CustomEvent('openContactModal', {
      detail: { 
        sectionType: sectionType,
        contactData: contact,
        isEdit: true
      }
    });
    window.dispatchEvent(event);
    setShowMenu(false);
  };

  const handleDelete = async () => {
    if (!isReady) return;
    if (!contact.id) {
      console.error('Contact ID is missing');
      return;
    }

    setIsDeleting(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts/${contact.id}`,
        await getAuthConfig()
      );
      
      // First call the onSuccess prop
      onSuccess();
      
      // Then dispatch the refresh event
      window.dispatchEvent(new Event('refreshContacts'));
      
      setShowDeleteConfirm(false);
    } catch (error) {
      // Enhanced error logging for backend response
      if (error.response) {
        console.error('Error deleting contact:', error.response.data);
      } else {
        console.error('Error deleting contact:', error.message);
      }
      alert('Failed to delete contact. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative">
      <button 
        className="p-1 hover:bg-gray-100 rounded-full"
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
          setShowDeleteConfirm(false);
        }}
      >
        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </button>

      {showMenu && !showDeleteConfirm && (
        <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-10">
          <button
            className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100 flex items-center"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteConfirm(true);
              setShowMenu(false);
            }}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Remove
          </button>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 p-3 z-10">
          <p className="text-sm text-gray-600 mb-2">Are you sure you want to delete this contact?</p>
          <div className="flex justify-end space-x-2">
            <button
              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(false);
              }}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              className={`px-2 py-1 text-xs text-white bg-red-600 hover:bg-red-700 rounded flex items-center ${
                isDeleting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 
